import React, { useState } from "react";
import { Button, Card, CardBody, CardTitle, Col, Row } from "reactstrap";
import { useFormik } from "formik";
import { BulkProductType } from "types";
import { useNavigate } from "react-router-dom";
import { useBulkProductCreate } from "hooks/useProduct";
import "react-datepicker/dist/react-datepicker.css";
import "assets/scss/datatables.scss";
import { AppRoutes } from "../../../constants";

import { toast } from "react-toastify";
import { ExampleFileModal } from "./ExampleFileModal";
import { BulkUploadInput } from "./BulkUploadInput";

export const BulkProductCreation = () => {
  const navigate = useNavigate();
  const { mutate: bulkUpload } = useBulkProductCreate();
  const [showExampleModal, setShowExampleModal] = useState<boolean>(false);

  const bulkCreateValidation = useFormik<BulkProductType>({
    initialValues: {
      spreadsheet: [],
    },
    onSubmit: async (values) => {
      if (values.spreadsheet.length < 1) {
        toast.error("Select files to continue.");
        return;
      }
      const payload = {
        spreadsheet: values.spreadsheet,
      };
      bulkUpload(payload, {
        onSuccess: (data) => {
          console.log("Response: ", data);
          navigate(AppRoutes.PRODUCTS);
        },
      });
    },
  });

  const fileRequirements = [
    "The file should be in either Excel or CSV format.",
    {
      title: "Each product entry must include the following:",
      items: [
        {
          label: "name:",
          description: (
            <>
              Unique name for product.{" "}
              <strong>
                <em>Required</em>
              </strong>
            </>
          ),
        },
        {
          label: "description:",
          description: (
            <>
              A description of the product{" "}
              <strong>
                <em>Required</em>
              </strong>
            </>
          ),
        },
        {
          label: "cost_price:",
          description: (
            <>
              The cost price of the product.{" "}
              <strong>
                <em>Required</em>
              </strong>
            </>
          ),
        },
        {
          label: "selling_price:",
          description: (
            <>
              The selling price of the product.{" "}
              <strong>
                <em>Required</em>
              </strong>
            </>
          ),
        },
        {
          label: "allow_taxes:",
          description: (
            <>
              Indicates if taxes are allowed for this product.{" "}
              <strong>
                <em>Required</em>
              </strong>
            </>
          ),
        },
        {
          label: "weight:",
          description: (
            <>
              The weight of the product.{" "}
              <strong>
                <em>Optional</em>
              </strong>
            </>
          ),
        },
        {
          label: "weight_unit:",
          description: (
            <>
              The unit of measurement for weight. It is required only if weight
              is added.{" "}
              <strong>
                <em>Optional</em>
              </strong>
            </>
          ),
        },
        {
          label: "quantity:",
          description: (
            <>
              The available quantity of the product.{" "}
              <strong>
                <em>Required</em>
              </strong>
            </>
          ),
        },
        {
          label: "reorder_quantity:",
          description: (
            <>
              The quantity at which to reorder the product.{" "}
              <strong>
                <em>Required</em>
              </strong>
            </>
          ),
        },
        {
          label: "category_id:",
          description: (
            <>
              The ID of the category the product belongs to.{" "}
              <strong>
                <em>Required</em>
              </strong>
            </>
          ),
        },
        {
          label: "branch_id:",
          description: (
            <>
              The ID of the branch where the product is available.{" "}
              <strong>
                <em>Required</em>
              </strong>
            </>
          ),
        },
        {
          label: "supplier_id:",
          description: (
            <>
              The ID of the supplier for the product.{" "}
              <strong>
                <em>Required</em>
              </strong>
            </>
          ),
        },
        {
          label: "sell_oos:",
          description: (
            <>
              Indicates if the product can be sold out of stock.{" "}
              <strong>
                <em>Required</em>
              </strong>
            </>
          ),
        },
        {
          label: "expiry_date:",
          description: (
            <>
              The expiry date of the product.{" "}
              <strong>
                <em>Optional</em>
              </strong>
            </>
          ),
        },
        {
          label: "barcode:",
          description: (
            <>
              The barcode for scanning the product.{" "}
              <strong>
                <em>Optional</em>
              </strong>
            </>
          ),
        },
      ],
    },
    "Ensure that the cost price and selling price are numeric values greater than or equal to zero.",
    "If applicable, include the expiry date in the format YYYY-MM-DD.",
    "Please review your file for any missing or incorrect information before uploading to avoid errors.",
  ];

  return (
    <React.Fragment>
      <Card>
        <CardBody>
          <Row>
            <Col xl="5" xxl="4">
              <BulkUploadInput
                setFieldValue={bulkCreateValidation.setFieldValue}
              />

              <div className="d-flex justify-content-center mt-4">
                <Button
                  color="primary"
                  className="mb-4 me-3"
                  onClick={() => bulkCreateValidation.handleSubmit()}
                >
                  Upload File
                </Button>
                <Button
                  color="primary"
                  className="animate__animated animate__pulse mb-4"
                  onClick={() => {
                    setShowExampleModal(true);
                  }}
                >
                  View Example
                </Button>
              </div>
              <div>
                <span
                  className="badge badge-soft-danger p-4 font-size-12 w-100 mb-4"
                  style={{
                    whiteSpace: "normal",
                    wordWrap: "break-word",
                    lineHeight: "1.5",
                  }}
                >
                  <i className="mdi mdi-alert-circle-outline mdi-24px m-0 p-0"></i>
                  <br />
                  Disclaimer: Any errors resulting from not following the above
                  instructions are not the responsibility of the application.
                  <br />
                  Please ensure that your file meets all requirements before
                  uploading.
                </span>
              </div>
            </Col>
            <Col xl="7" xxl="8">
              <Card>
                <CardBody>
                  <CardTitle tag="h4">File Requirements</CardTitle>
                  <p>
                    To successfully upload your products, please ensure that
                    your file meets the following requirements:
                  </p>
                  <ul>
                    {fileRequirements.map((requirement, index) => {
                      if (typeof requirement === "string") {
                        return (
                          <li key={index} style={{ marginBottom: "10px" }}>
                            {requirement}
                          </li>
                        );
                      } else if (requirement.items) {
                        return (
                          <li key={index} style={{ marginBottom: "10px" }}>
                            {requirement.title}
                            <ul>
                              {requirement.items.map((item, itemIndex) => (
                                <li
                                  key={itemIndex}
                                  style={{ marginBottom: "10px" }}
                                >
                                  <strong>{item.label}</strong>{" "}
                                  {item.description}
                                </li>
                              ))}
                            </ul>
                          </li>
                        );
                      }
                      return null;
                    })}
                  </ul>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </CardBody>
      </Card>

      <ExampleFileModal
        show={showExampleModal}
        onCloseClick={() => setShowExampleModal(false)}
      />
    </React.Fragment>
  );
};
